import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TeethSelector from './TeethSelector';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../../services/apiClient';
import jsPDF from 'jspdf';
import medidentBeBg1 from '../../assets/medident-bern-fr-1.png';
import medidentBeBg2 from '../../assets/medident-bern-fr-2.png';
import medidentBeBg3 from '../../assets/medident-bern-fr-3.png';
import medidentBeBg4 from '../../assets/medident-bern-fr-4.png';
import medidentBeBg5 from '../../assets/medident-bern-fr-5.png';
import colors from '../../config/colors';
import { normalizeTeethListFields, parseTeethList } from '../../utils/teethList';
import EuropeanDateInput from '../common/EuropeanDateInput';

export const medidentBeDefaultFormData = {
  'patient-avs': '',
  'patient-name': '',
  'patient-birth': '',
  'clinic-name': '',
  'clinic-address': '',
  'clinic-zip-city': '',
  'treating-dentist': '',
  'consultation-date': '',
  'missing-teeth': '',
  'examens': '',
  'hygiene-sanitaire-motivation': '',
  'maxillaires-parties-molles': '',
  'etat-parodontal': '',
  'dents-conservateurs': '',
  'dents-radiculaire': '',
  'dents-caries-fractures': '',
  'dents-pronostic-incertain': '',
  'dents-remplacement-prothese': '',
  'treatment-objective': '',
  'motifs-description': '',
  'mesures-immediates': '',
  'plan-traitement': '',
  'justification-radiculaire': '',
  'justification-resection': '',
  'other-treatments-5y': '',
  'other-treatments-5y-details': '',
  'attestation-18-months': '',
  'recall-system': '',
  'restrictions': '',
  'restrictions-reason': '',
  'compliance': '',
  'social-environment': '',
  'place-date': '',
};

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const wrapText = (doc, text, x, y, maxWidth, lineHeight, fontSize) => {
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(String(text || ''), maxWidth);
  lines.forEach((line, i) => {
    doc.text(line, x, y + i * lineHeight);
  });
  return y + lines.length * lineHeight;
};

const MEDIDENT_IMG_W = 1191;
const MEDIDENT_IMG_H = 1684;

const isUpperTooth = (tooth) => {
  const n = Number(tooth);
  return (n >= 11 && n <= 28) || (n >= 51 && n <= 68);
};

// Tooth number centers on page-2 missing-teeth grid (image px from PNG ink).
// Layout: 8 7 6 5 4 3 2 1 | 1 2 3 4 5 6 7 8
// Permanent: 18-11 | 21-28 (upper), 48-41 | 31-38 (lower)
// Deciduous share the same digit columns: 55-51→15-11, 61-65→21-25, 85-81→45-41, 71-75→31-35
const toothGridX = {
  18: 182, 17: 236, 16: 290, 15: 344, 14: 398, 13: 453, 12: 506, 11: 559,
  21: 670, 22: 722, 23: 776, 24: 830, 25: 884, 26: 938, 27: 992, 28: 1046,
  48: 182, 47: 236, 46: 290, 45: 344, 44: 398, 43: 453, 42: 506, 41: 559,
  31: 670, 32: 722, 33: 776, 34: 830, 35: 884, 36: 938, 37: 992, 38: 1046,
  // Deciduous (temporary)
  55: 344, 54: 398, 53: 453, 52: 506, 51: 559,
  61: 670, 62: 722, 63: 776, 64: 830, 65: 884,
  85: 344, 84: 398, 83: 453, 82: 506, 81: 559,
  71: 670, 72: 722, 73: 776, 74: 830, 75: 884,
};

const teethGridY = {
  // Centers of printed digit rows (separator line at ~1065)
  'missing-teeth': { upper: 1035, lower: 1091 },
};

const MedidentBeForm = ({ initialData, onSave }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(() => ({
    ...medidentBeDefaultFormData,
    ...normalizeTeethListFields(initialData || {}),
  }));

  useEffect(() => {
    setFormData({
      ...medidentBeDefaultFormData,
      ...normalizeTeethListFields(initialData || {}),
    });
  }, [initialData]);

  const debouncedSave = useRef(
    debounce((data) => {
      if (typeof onSave === 'function') {
        onSave(data);
      }
    }, 500)
  ).current;

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      debouncedSave(next);
      return next;
    });
  };

  const isFieldEmpty = (field) => {
    const value = formData[field];
    if (typeof value === 'boolean') {
      return !value;
    }
    return value == null || String(value).trim() === '';
  };

  const isSectionComplete = ({ sectionFields = [], requireAtLeastOne = false, isOptional = false }) => {
    if (!sectionFields.length) {
      return false;
    }
    if (requireAtLeastOne) {
      return sectionFields.some((field) => !isFieldEmpty(field));
    }
    if (isOptional) {
      return sectionFields.some((field) => !isFieldEmpty(field));
    }
    return sectionFields.every((field) => !isFieldEmpty(field));
  };

  const renderAccordionSection = ({
    title,
    children,
    sectionFields = [],
    isOptional = false,
    requireAtLeastOne = false,
  }) => {
    const hasEmptyMandatory = !isOptional && (
      requireAtLeastOne
        ? !sectionFields.some((field) => !isFieldEmpty(field))
        : sectionFields.some((field) => isFieldEmpty(field))
    );
    const showFilled = isSectionComplete({ sectionFields, requireAtLeastOne, isOptional });

    return (
      <Disclosure>
        {({ open }) => (
          <div className={`mb-4 rounded-lg ${open ? 'bg-white shadow-md' : ''}`}>
            <Disclosure.Button
              className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium
                bg-white text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none
                focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50"
            >
              <span>{title}</span>
              <div className="flex items-center gap-2">
                {!isOptional && hasEmptyMandatory ? (
                  <span className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded">
                    {t('insurance.labels.required')}
                  </span>
                ) : showFilled ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-600 rounded">
                    {t('insurance.labels.filled')}
                  </span>
                ) : null}
                <ChevronUpIcon className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500 ml-2`} />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">
              {children}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };

  const renderField = (field, label, type = 'text', required = false) => (
    <div className="flex flex-col space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {type === 'textarea' ? (
        <textarea
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          rows={4}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mt-1 block w-full"
        />
      ) : type === 'date' ? (
        <EuropeanDateInput
          value={formData[field] || ''}
          onChange={(value) => handleChange(field, value)}
          required={required}
        />
      ) : (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        />
      )}
    </div>
  );

  const renderRadioGroup = (field, label, options, required = false) => (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <div className="flex flex-wrap items-center gap-4">
        {options.map(({ value, labelKey }) => (
          <label key={value} className="inline-flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name={field}
              checked={formData[field] === value}
              onChange={() => handleChange(field, value)}
              className="h-4 w-4 border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
            />
            {t(`medidentBe.fields.${labelKey}`)}
          </label>
        ))}
      </div>
    </div>
  );

  // Crossed lines centered on (ix, iy) — more reliable than text baseline metrics
  const drawMarkX = (doc, ix, iy, halfSizeImg = 6, lineWidthImg = 1.1) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    const cx = ix * sx;
    const cy = iy * sy;
    const hx = halfSizeImg * sx;
    const hy = halfSizeImg * sy;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(lineWidthImg * Math.min(sx, sy));
    doc.line(cx - hx, cy - hy, cx + hx, cy + hy);
    doc.line(cx - hx, cy + hy, cx + hx, cy - hy);
  };

  const drawImgText = (doc, text, ix, iy, maxWidthImg, lineHeightImg, fontSize) => {
    if (!text) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    wrapText(doc, text, ix * sx, iy * sy, maxWidthImg * sx, (lineHeightImg + 2) * sy, fontSize);
  };

  const drawTeethOnGrid = (doc) => {
    const row = teethGridY['missing-teeth'];
    parseTeethList(formData['missing-teeth']).forEach((tooth) => {
      const x = toothGridX[tooth];
      if (x == null) return;
      const y = isUpperTooth(tooth) ? row.upper : row.lower;
      // Thicker/larger crosses so missing teeth remain visible on the printed schema
      drawMarkX(doc, x, y, 9, 3.2);
    });
  };

  const handleGeneratePDF = async () => {
    if (typeof onSave === 'function') {
      onSave(formData);
    }

    const doc = new jsPDF({ unit: 'px', format: 'a4' });
    const getImageBase64 = (src) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = src;
      });

    const [bg1, bg2, bg3, bg4, bg5] = await Promise.all([
      getImageBase64(medidentBeBg1),
      getImageBase64(medidentBeBg2),
      getImageBase64(medidentBeBg3),
      getImageBase64(medidentBeBg4),
      getImageBase64(medidentBeBg5),
    ]);

    let signatureDataUrl = null;
    let signatureType = 'JPEG';
    try {
      const profile = await apiClient.get('/api/profile');
      if (profile?.signature_image) {
        signatureDataUrl = profile.signature_image;
        signatureType = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      }
    } catch (e) {
      // ignore
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;

    // Page 1 — instructions only
    doc.addImage(bg1, 'PNG', 0, 0, pageWidth, pageHeight);

    // Page 2 — patient, cabinet, examens, missing teeth
    // Coordinates from PDF text / PNG ink scaled to 1191×1684.
    doc.addPage();
    doc.addImage(bg2, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);

    // Baselines from PDF text (scaled to 1191×1684 PNG)
    const page2Text = [
      ['patient-avs', 241, 198],
      ['patient-name', 290, 249],
      ['patient-birth', 339, 300],
      ['clinic-name', 327, 433],
      ['clinic-address', 238, 483],
      ['clinic-zip-city', 261, 534],
      ['treating-dentist', 487, 585],
      ['consultation-date', 379, 834],
    ];
    page2Text.forEach(([key, ix, iy]) => {
      if (formData[key]) {
        doc.text(String(formData[key]), ix * sx, iy * sy);
      }
    });

    drawTeethOnGrid(doc);
    // Multiline blocks start just below their labels
    drawImgText(doc, formData.examens, 150, 1225, 900, 12, 8);
    drawImgText(doc, formData['hygiene-sanitaire-motivation'], 150, 1305, 900, 12, 8);
    drawImgText(doc, formData['maxillaires-parties-molles'], 150, 1460, 900, 12, 8);

    // Page 3 — état dentaire + objectif
    doc.addPage();
    doc.addImage(bg3, 'PNG', 0, 0, pageWidth, pageHeight);
    drawImgText(doc, formData['etat-parodontal'], 150, 200, 900, 12, 8);
    drawImgText(doc, formData['dents-conservateurs'], 150, 325, 900, 12, 8);
    drawImgText(doc, formData['dents-radiculaire'], 150, 475, 900, 12, 8);
    drawImgText(doc, formData['dents-caries-fractures'], 150, 625, 900, 12, 8);
    drawImgText(doc, formData['dents-pronostic-incertain'], 150, 775, 900, 12, 8);
    drawImgText(doc, formData['dents-remplacement-prothese'], 150, 960, 900, 12, 8);

    // Radios: hollow gray circle centers (PNG ink bbox midpoints)
    if (formData['treatment-objective'] === 'attendre') {
      drawMarkX(doc, 154, 1131);
    } else if (formData['treatment-objective'] === 'soins') {
      drawMarkX(doc, 159, 1299);
    }
    drawImgText(doc, formData['motifs-description'], 150, 1445, 900, 12, 8);

    // Page 4 — planification
    doc.addPage();
    doc.addImage(bg4, 'PNG', 0, 0, pageWidth, pageHeight);
    drawImgText(doc, formData['mesures-immediates'], 150, 285, 900, 12, 8);
    drawImgText(doc, formData['plan-traitement'], 150, 460, 900, 12, 8);
    drawImgText(doc, formData['justification-radiculaire'], 150, 640, 900, 12, 8);
    drawImgText(doc, formData['justification-resection'], 150, 815, 900, 12, 8);

    if (formData['other-treatments-5y'] === 'oui') {
      drawMarkX(doc, 145, 1021);
      drawImgText(doc, formData['other-treatments-5y-details'], 330, 1028, 650, 12, 8);
    } else if (formData['other-treatments-5y'] === 'non') {
      drawMarkX(doc, 145, 1144);
    }

    if (formData['attestation-18-months'] === 'oui') {
      drawMarkX(doc, 155, 1510);
    } else if (formData['attestation-18-months'] === 'non') {
      drawMarkX(doc, 266, 1510);
    }

    // Page 5 — rappel, restrictions, signature
    doc.addPage();
    doc.addImage(bg5, 'PNG', 0, 0, pageWidth, pageHeight);

    if (formData['recall-system'] === 'oui') {
      drawMarkX(doc, 155, 252);
    } else if (formData['recall-system'] === 'non') {
      drawMarkX(doc, 266, 252);
    }

    if (formData.restrictions === 'aucun') {
      drawMarkX(doc, 154, 392);
    } else if (formData.restrictions === 'partiellement') {
      drawMarkX(doc, 333, 392);
    } else if (formData.restrictions === 'nettement') {
      drawMarkX(doc, 633, 392);
    }
    drawImgText(doc, formData['restrictions-reason'], 370, 472, 650, 12, 8);

    if (formData.compliance === 'bien') {
      drawMarkX(doc, 154, 622);
    } else if (formData.compliance === 'douteux') {
      drawMarkX(doc, 333, 622);
    } else if (formData.compliance === 'mauvais') {
      drawMarkX(doc, 546, 622);
    }

    if (formData['social-environment'] === 'garanti') {
      drawMarkX(doc, 154, 803);
    } else if (formData['social-environment'] === 'confus') {
      drawMarkX(doc, 333, 803);
    } else if (formData['social-environment'] === 'pas-garanti') {
      drawMarkX(doc, 545, 803);
    }

    // Clear baked-in place/date ("Neuchâtel, le …") then rewrite
    doc.setFillColor(255, 255, 255);
    doc.rect(140 * sx, 1205 * sy, 420 * sx, 40 * sy, 'F');
    if (formData['place-date']) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(String(formData['place-date']), 150 * sx, 1235 * sy);
    }

    if (signatureDataUrl) {
      try {
        doc.addImage(signatureDataUrl, signatureType, 620 * sx, 1200 * sy, 160 * sx, 55 * sy);
      } catch (e) {
        // ignore invalid signature
      }
    }

    doc.save('medident-bern-form.pdf');
  };

  const PATIENT_CABINET_FIELDS = [
    'patient-avs',
    'patient-name',
    'patient-birth',
    'clinic-name',
    'clinic-address',
    'clinic-zip-city',
    'treating-dentist',
    'consultation-date',
  ];

  const EXAMENS_FIELDS = [
    'missing-teeth',
    'examens',
    'hygiene-sanitaire-motivation',
    'maxillaires-parties-molles',
  ];

  const STATUS_FIELDS = [
    'etat-parodontal',
    'dents-conservateurs',
    'dents-radiculaire',
    'dents-caries-fractures',
    'dents-pronostic-incertain',
    'dents-remplacement-prothese',
  ];

  const OBJECTIF_FIELDS = ['treatment-objective', 'motifs-description'];

  const PLANIFICATION_FIELDS = [
    'mesures-immediates',
    'plan-traitement',
    'justification-radiculaire',
    'justification-resection',
    'other-treatments-5y',
    'other-treatments-5y-details',
  ];

  const ATTESTATION_FIELDS = [
    'attestation-18-months',
    'recall-system',
    'restrictions',
    'restrictions-reason',
    'compliance',
    'social-environment',
    'place-date',
  ];

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('consultation.steps.medidentBe')}</h2>
        <button
          type="button"
          onClick={handleGeneratePDF}
          style={{
            backgroundColor: colors.primary.selected,
            color: 'white',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            border: 'none',
          }}
        >
          {t('insurance.buttons.generatePdf', 'Generate PDF')}
        </button>
      </div>

      {renderAccordionSection({
        title: t('medidentBe.sections.patientCabinet'),
        sectionFields: PATIENT_CABINET_FIELDS,
        children: (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">{t('medidentBe.fields.patient')}</h3>
            {renderField('patient-avs', t('medidentBe.fields.patientAvs'), 'text', true)}
            {renderField('patient-name', t('medidentBe.fields.patientName'), 'text', true)}
            {renderField('patient-birth', t('medidentBe.fields.patientBirth'), 'date', true)}
            <h3 className="font-medium text-gray-900">{t('medidentBe.fields.cabinet')}</h3>
            {renderField('clinic-name', t('medidentBe.fields.clinicName'), 'text', true)}
            {renderField('clinic-address', t('medidentBe.fields.clinicAddress'), 'text', true)}
            {renderField('clinic-zip-city', t('medidentBe.fields.clinicZipCity'), 'text', true)}
            {renderField('treating-dentist', t('medidentBe.fields.treatingDentist'), 'text', true)}
            {renderField('consultation-date', t('medidentBe.fields.consultationDate'), 'date', true)}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentBe.sections.examens'),
        sectionFields: EXAMENS_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('medidentBe.fields.missingTeeth')}</p>
              <TeethSelector
                selectedTeeth={formData['missing-teeth'] || ''}
                onTeethSelect={(value) => handleChange('missing-teeth', value)}
              />
            </div>
            {renderField('examens', t('medidentBe.fields.examens'), 'textarea')}
            {renderField('hygiene-sanitaire-motivation', t('medidentBe.fields.hygieneSanitaireMotivation'), 'textarea')}
            {renderField('maxillaires-parties-molles', t('medidentBe.fields.maxillairesPartiesMolles'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentBe.sections.statusDentaire'),
        sectionFields: STATUS_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderField('etat-parodontal', t('medidentBe.fields.etatParodontal'), 'textarea')}
            {renderField('dents-conservateurs', t('medidentBe.fields.dentsConservateurs'), 'textarea')}
            {renderField('dents-radiculaire', t('medidentBe.fields.dentsRadiculaire'), 'textarea')}
            {renderField('dents-caries-fractures', t('medidentBe.fields.dentsCariesFractures'), 'textarea')}
            {renderField('dents-pronostic-incertain', t('medidentBe.fields.dentsPronosticIncertain'), 'textarea')}
            {renderField('dents-remplacement-prothese', t('medidentBe.fields.dentsRemplacementProthese'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentBe.sections.objectif'),
        sectionFields: OBJECTIF_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderRadioGroup('treatment-objective', t('medidentBe.fields.treatmentObjective'), [
              { value: 'attendre', labelKey: 'attendre' },
              { value: 'soins', labelKey: 'soins' },
            ])}
            {renderField('motifs-description', t('medidentBe.fields.motifsDescription'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentBe.sections.planification'),
        sectionFields: PLANIFICATION_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderField('mesures-immediates', t('medidentBe.fields.mesuresImmediates'), 'textarea')}
            {renderField('plan-traitement', t('medidentBe.fields.planTraitement'), 'textarea')}
            {renderField('justification-radiculaire', t('medidentBe.fields.justificationRadiculaire'), 'textarea')}
            {renderField('justification-resection', t('medidentBe.fields.justificationResection'), 'textarea')}
            {renderRadioGroup('other-treatments-5y', t('medidentBe.fields.otherTreatments5y'), [
              { value: 'oui', labelKey: 'oui' },
              { value: 'non', labelKey: 'non' },
            ])}
            {formData['other-treatments-5y'] === 'oui' &&
              renderField('other-treatments-5y-details', t('medidentBe.fields.otherTreatments5yDetails'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentBe.sections.attestation'),
        sectionFields: ATTESTATION_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderRadioGroup('attestation-18-months', t('medidentBe.fields.attestation18Months'), [
              { value: 'oui', labelKey: 'oui' },
              { value: 'non', labelKey: 'non' },
            ])}
            {renderRadioGroup('recall-system', t('medidentBe.fields.recallSystem'), [
              { value: 'oui', labelKey: 'oui' },
              { value: 'non', labelKey: 'non' },
            ])}
            {renderRadioGroup('restrictions', t('medidentBe.fields.restrictions'), [
              { value: 'aucun', labelKey: 'restrictionsAucun' },
              { value: 'partiellement', labelKey: 'restrictionsPartiellement' },
              { value: 'nettement', labelKey: 'restrictionsNettement' },
            ])}
            {renderField('restrictions-reason', t('medidentBe.fields.restrictionsReason'), 'textarea')}
            {renderRadioGroup('compliance', t('medidentBe.fields.compliance'), [
              { value: 'bien', labelKey: 'complianceBien' },
              { value: 'douteux', labelKey: 'complianceDouteux' },
              { value: 'mauvais', labelKey: 'complianceMauvais' },
            ])}
            {renderRadioGroup('social-environment', t('medidentBe.fields.socialEnvironment'), [
              { value: 'garanti', labelKey: 'socialGaranti' },
              { value: 'confus', labelKey: 'socialConfus' },
              { value: 'pas-garanti', labelKey: 'socialPasGaranti' },
            ])}
            {renderField('place-date', t('medidentBe.fields.placeDate'))}
          </div>
        ),
      })}
    </form>
  );
};

export default MedidentBeForm;
